import React, { useMemo } from 'react';

import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TablePagination from '@mui/material/TablePagination';
import { OrderValue, TableParams } from 'types/common';
import EnhancedTableHead from './TableHead';
import EnhancedTableBody from './TableBody';
import { ITableHeadCell, ITableComponents } from './types';

export interface TableRemotePaginationProps<T> {
    rows: T[];
    columns: ITableHeadCell[];
    tableParams: TableParams;
    totalCount: number;
    deleteWarning?: React.ReactNode;
    warningHeader?: string;
    components?: ITableComponents<T>;
    className?: string;
    rowsPerPageOptions?: number[];
    hidePagination?: boolean;
    children?: React.ReactNode;
    isRowCountDisabled?: boolean;
    defaultRowsPerPage?: number;
    selected?: string[];
    onSort: (order: OrderValue, orderBy: string) => void;
    onSetPage: (page: number) => void;
    onSetRowsPerPage: (rowsPerPage: number) => void;
    onSelect?: (selected: string[]) => void;
    onEditAction?: (rowId: string) => void;
    onDeleteAction?: (rowId: string) => void;
}

export const TableRemotePagination = <T extends { id: string }>({
    rows = [],
    columns,
    totalCount,
    tableParams,
    deleteWarning,
    warningHeader,
    components = {},
    rowsPerPageOptions = [5, 10, 25],
    hidePagination = false,
    className,
    children,
    isRowCountDisabled = false,
    defaultRowsPerPage,
    selected = [],
    onSort,
    onSetPage,
    onSetRowsPerPage,
    onEditAction,
    onDeleteAction,
    onSelect,
}: TableRemotePaginationProps<T>) => {
    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onSelect) {
            if (event.target.checked) {
                const newSelected = rows.map((n) => n.id);
                onSelect(newSelected);
                return;
            }
            onSelect([]);
        }
    };

    const selectRowHandler = (event: React.MouseEvent<unknown>, name: string) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }

        if (onSelect) {
            onSelect(newSelected);
        }
    };

    const handleRequestSort = (_: React.MouseEvent<unknown>, property: string) => {
        const isAsc = tableParams.orderBy === property && tableParams.sortOrder === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        onSort(newOrder, property);
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        onSetPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        onSetRowsPerPage(parseInt(event.target.value, 10));
    };

    const withCheckbox = useMemo(() => !!onSelect, [onSelect]);

    return (
        <Paper className={className}>
            {components.Toolbar && <components.Toolbar />}
            <TableContainer>
                <Table aria-labelledby="tableTitle" size="medium" stickyHeader sx={{ minWidth: 750 }}>
                    <EnhancedTableHead
                        columns={columns}
                        order={tableParams.sortOrder}
                        orderBy={tableParams.orderBy}
                        components={components}
                        rowCount={rows.length}
                        numSelected={selected.length}
                        showActions={!!onEditAction || !!onDeleteAction}
                        onSelectAllClick={handleSelectAllClick}
                        onRequestSort={handleRequestSort}
                        withCheckbox={withCheckbox}
                    />
                    {rows.length > 0 && (
                        <EnhancedTableBody
                            rows={rows}
                            columns={columns}
                            onEditAction={onEditAction}
                            onDeleteAction={onDeleteAction}
                            deleteWarning={deleteWarning}
                            warningHeader={warningHeader}
                            components={components}
                            selected={selected}
                            onSelectRow={selectRowHandler}
                            withCheckbox={withCheckbox}
                        />
                    )}
                </Table>
            </TableContainer>
            {!hidePagination && (
                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component="div"
                    count={totalCount}
                    rowsPerPage={defaultRowsPerPage ?? tableParams.pageSize}
                    page={tableParams.page}
                    showFirstButton
                    showLastButton
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    slotProps={{ select: { disabled: isRowCountDisabled } }}
                    labelRowsPerPage="Rows per range:"
                />
            )}
            {children}
        </Paper>
    );
};
